import React from "react";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";
import UsePageTitle from "../components/UsePageTitle";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import SuccessImage1 from "../resources/images/Success_Img_1.png";
import CheckMark from "../resources/images/Check.svg";
import SuccessImage2 from "../resources/images/Success_Img_2.png";
import { motion } from "framer-motion";
import axios from "axios";

const FindTalent = () => {
  UsePageTitle("Find Talent | Bravix Group");
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      companyName: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().required("Phone number is required"),
      companyName: Yup.string().required("Company name is required"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      console.log("Form Values before submission:", values);
      try {
        const response = await axios.post(
          "https://bravixgroup.com/api/v1/connect-us",
          values
        );
        console.log("API Response:", response.data);
        if (!response.data.error) {
          enqueueSnackbar(
            response.data.message || "Form submitted successfully!",
            {
              variant: "success",
            }
          );
          resetForm();
        } else {
          enqueueSnackbar(response.data.message || "Something went wrong!", {
            variant: "error",
          });
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "Failed to submit the form.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const leftContainerVariant = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const childVariant = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const pointContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const pointItem = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <>
      <Navbar />
      <section className="find_talent_section">
        <div className="container h-100">
          <div className="row h-100">
            <div className="find_talent_content_main">
              <div className="find_talent_content">
                <motion.div
                  className="find_talent_content_left"
                  variants={leftContainerVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ amount: 0.3 }}
                >
                  <motion.h5 variants={childVariant}>
                    TALENT SOLUTIONS FOR <br /> CONSTRUCTION LEADERS
                  </motion.h5>
                  <motion.p variants={childVariant}>
                    Our construction recruiters connect you with qualified
                    professionals across the country. Whether you need someone
                    for an urgent project or you're building your team for the
                    long haul, we find the right people to help your projects
                    succeed from planning through completion.
                  </motion.p>
                  <motion.h5 variants={childVariant}>
                    Let’s build together!
                  </motion.h5>
                </motion.div>

                <div className="find_talent_content_right">
                  <form
                    noValidate
                    onSubmit={formik.handleSubmit}
                    autoComplete="off"
                    method="POST"
                  >
                    <div className="input_main">
                      <div className="input_group">
                        <input
                          type="text"
                          className="form-control"
                          id="firstName"
                          name="firstName"
                          placeholder="First Name"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.firstName &&
                          formik.errors.firstName && (
                            <div className="error">
                              {formik.errors.firstName}
                            </div>
                          )}
                      </div>
                      <div className="input_group">
                        <input
                          type="text"
                          className="form-control"
                          id="lastName"
                          name="lastName"
                          placeholder="Last Name"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {formik.touched.lastName && formik.errors.lastName && (
                          <div className="error">{formik.errors.lastName}</div>
                        )}
                      </div>
                    </div>

                    <div className="input_group">
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        name="email"
                        placeholder="Email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.email && formik.errors.email && (
                        <div className="error">{formik.errors.email}</div>
                      )}
                    </div>

                    <div className="input_group">
                      <PhoneInput
                        id="phone"
                        name="phone"
                        defaultCountry="US"
                        value={formik.values.phone}
                        onChange={(value) =>
                          formik.setFieldValue("phone", value)
                        }
                        placeholder="Phone"
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.phone && formik.errors.phone && (
                        <div className="error">{formik.errors.phone}</div>
                      )}
                    </div>

                    <div className="input_group">
                      <input
                        type="text"
                        className="form-control"
                        id="companyName"
                        name="companyName"
                        placeholder="Company Name"
                        value={formik.values.companyName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.companyName &&
                        formik.errors.companyName && (
                          <div className="error">
                            {formik.errors.companyName}
                          </div>
                        )}
                    </div>

                    <button
                      type="submit"
                      className="submit_btn"
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="success_section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="success_top">
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ amount: 0.3 }}
                >
                  Success
                </motion.p>
                <motion.h5
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                  viewport={{ amount: 0.3 }}
                >
                  Your Success drives america
                </motion.h5>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="success_card_main">
                <img
                  src={SuccessImage1}
                  alt="Success"
                  className="success_img"
                />

                <div className="success_card">
                  <motion.p
                    initial="hidden"
                    whileInView="show"
                    viewport={{ amount: 0.3 }}
                    variants={fadeInLeft}
                  >
                    We deliver customized recruitment solutions for the
                    commercial, multifamily, and related construction sectors.
                    Our expertise spans Field Personnel, Office Staff,
                    Operations roles, and beyond. Backed by a robust network of
                    industry professionals, we're here to help you advance your
                    construction career with confidence.
                  </motion.p>
                  <motion.div
                    className="point_main"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ amount: 0.3 }}
                    variants={pointContainer}
                  >
                    <motion.div className="point" variants={pointItem}>
                      <p>
                        Tailored recruitment solutions - we operate as a
                        seamless extension of your team, with a deep
                        understanding of the talent you need to succeed
                      </p>
                    </motion.div>
                    <motion.div className="point" variants={pointItem}>
                      <p>
                        ⁠⁠Industry Expertise - sound knowledge of the
                        intricacies within commercial construction
                      </p>
                    </motion.div>
                    <motion.div className="point" variants={pointItem}>
                      <p>
                        ⁠⁠Rest Easy - your business is backed by our contract
                        guarantees
                      </p>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="solution_section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="solution_top">
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  viewport={{ amount: 0.3 }}
                >
                  Solutions
                </motion.p>
                <motion.h5
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
                  viewport={{ amount: 0.3 }}
                >
                  Tailored Solutions to Suit YOU{" "}
                </motion.h5>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="solution_card_main">
                <div className="solution_card">
                  <motion.p
                    initial="hidden"
                    whileInView="show"
                    viewport={{ amount: 0.3 }}
                    variants={fadeInLeft}
                  >
                    Bravix Group’s proven methodology is designed to adapt to
                    the evolving market. From the first client conversation to
                    candidate placement, we partner with industry specialists to
                    ensure successful outcomes. Our transparent approach
                    includes setting clear expectations and delivering tailored
                    search results within your preferred timeline.
                  </motion.p>
                  {/* <motion.div
                    className="point_main"
                    initial="hidden"
                    whileInView="show"
                    viewport={{ amount: 0.3 }}
                    variants={pointContainer}
                  >
                    <motion.div className="point" variants={pointItem}>
                      <img src={CheckMark} alt={CheckMark} />
                      <p>Direct Recruitment Solutions</p>
                    </motion.div>
                    <motion.div className="point" variants={pointItem}>
                      <img src={CheckMark} alt={CheckMark} />
                      <p>Flexible Contract and Temporary Staffing</p>
                    </motion.div>
                    <motion.div className="point" variants={pointItem}>
                      <img src={CheckMark} alt={CheckMark} />
                      <p>Collaborative Project Partnerships</p>
                    </motion.div>
                    <motion.div className="point" variants={pointItem}>
                      <img src={CheckMark} alt={CheckMark} />
                      <p>Specialized Executive Search</p>
                    </motion.div>
                  </motion.div> */}
                </div>
                <img
                  src={SuccessImage2}
                  alt={SuccessImage2}
                  className="solution_img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default FindTalent;
